import React, { useCallback, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import Toggle from '@/components/toggle';
import Typography from '@/components/typography';
import { useElevation } from '@/features/insights/elevation/useElevation';
import { useFlood } from '@/features/insights/flood/useFlood';
import { useSoil } from '@/features/insights/soils/useSoils';
import { OpacityChanger } from '@/features/insightsCatalog/components/opacityChanger';
import { AcresMapboxLayerIds } from '@/mapbox-layers';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';

type OverlayProps = {
  opacity: number;
};

const FloodOverlay: React.FC<OverlayProps> = ({ opacity }) => {
  const { mapInstance: map } = useMapboxContext();

  useFlood({ map });

  const updateOpacity = useCallback(() => {
    if (!map) return;
    if (map.getLayer(AcresMapboxLayerIds.FLOOD)) {
      map.setPaintProperty(
        AcresMapboxLayerIds.FLOOD,
        'fill-opacity',
        opacity / 100
      );
    }
    if (map.getLayer(AcresMapboxLayerIds.WETLANDS)) {
      map.setPaintProperty(
        AcresMapboxLayerIds.WETLANDS,
        'line-opacity',
        opacity / 100
      );
    }
  }, [opacity, map]);

  useEffect(() => {
    updateOpacity();
  }, [updateOpacity]);

  // This will make sure the opacity gets update when the layer is added if it wasn't initially
  useEffect(() => {
    if (!map) return () => {};

    map.on('styledata', updateOpacity);

    return () => {
      map.off('styledata', updateOpacity);
    };
  }, [map, updateOpacity]);

  return null;
};

const SoilsOverlay: React.FC<OverlayProps> = ({ opacity }) => {
  const { mapInstance: map } = useMapboxContext();

  useSoil({ map });

  const updateOpacity = useCallback(() => {
    if (!map) return;
    if (!map.getLayer(AcresMapboxLayerIds.SOIL)) {
      return;
    }
    map.setPaintProperty(
      AcresMapboxLayerIds.SOIL,
      'fill-opacity',
      opacity / 100
    );
  }, [opacity, map]);

  useEffect(() => {
    updateOpacity();
  }, [updateOpacity]);

  // This will make sure the opacity gets update when the layer is added if it wasn't initially
  useEffect(() => {
    if (!map) return () => {};

    map.on('styledata', updateOpacity);

    return () => {
      map.off('styledata', updateOpacity);
    };
  }, [map, updateOpacity]);
  return null;
};

const ElevationOverlay: React.FC<OverlayProps> = ({ opacity }) => {
  const { mapInstance: map } = useMapboxContext();

  useElevation({ interval: 10, map });

  const updateOpacity = useCallback(() => {
    if (!map) return;
    if (map.getLayer(AcresMapboxLayerIds.ELEVATION_COLOR)) {
      map.setPaintProperty(
        AcresMapboxLayerIds.ELEVATION_COLOR,
        'raster-opacity',
        opacity / 100
      );
    }
    if (map.getLayer(AcresMapboxLayerIds.ELEVATION_CONTOUR)) {
      map.setPaintProperty(
        AcresMapboxLayerIds.ELEVATION_CONTOUR,
        'line-opacity',
        opacity / 100
      );
    }
  }, [map, opacity]);

  useEffect(() => {
    updateOpacity();
  }, [updateOpacity]);

  // This will make sure the opacity gets update when the layer is added if it wasn't initially
  useEffect(() => {
    if (!map) return () => {};

    map.on('styledata', updateOpacity);

    return () => {
      map.off('styledata', updateOpacity);
    };
  }, [map, updateOpacity]);

  return null;
};

type Props = {};

export const InsightsOverlay: React.FC<Props> = () => {
  const [opacity, setOpacity] = useState<number>(60);
  const [elevationActive, setElevationActive] = useState<boolean>(false);
  const [soilsActive, setSoilsActive] = useState<boolean>(false);
  const [floodActive, setFloodActive] = useState<boolean>(false);

  return (
    <div className="flex flex-col gap-4 py-4">
      <Typography className="px-4 text-gray-100" variant="h5">
        Insight Overlays
      </Typography>

      {/* Toggle container */}
      <div
        className={twMerge(
          'flex flex-col divide-y-1 divide-gray-20 text-gray-100'
        )}
      >
        {/* Elevation */}
        <div className="flex flex-col gap-y-2 px-4 py-3">
          <label className="flex justify-between" htmlFor="elevation">
            <Typography variant="body2">Elevation</Typography>
            <Toggle
              active={elevationActive}
              id="elevation"
              setActive={(val) => {
                if (val) {
                  setSoilsActive(false);
                  setFloodActive(false);
                }
                setElevationActive(val);
              }}
            />
            {elevationActive && <ElevationOverlay opacity={opacity} />}
          </label>
          {/* Opacity Slider */}
          {elevationActive && (
            <OpacityChanger value={opacity} onChange={setOpacity} />
          )}
        </div>

        {/* Soils */}
        <div className="flex flex-col gap-y-2 px-4 py-3">
          <label className="flex justify-between" htmlFor="soils">
            <Typography variant="body2">Soils</Typography>
            <Toggle
              active={soilsActive}
              id="soils"
              setActive={(val) => {
                if (val) {
                  setElevationActive(false);
                  setFloodActive(false);
                }
                setSoilsActive(val);
              }}
            />
            {soilsActive && <SoilsOverlay opacity={opacity} />}
          </label>
          {/* Opacity Slider */}
          {soilsActive && (
            <OpacityChanger value={opacity} onChange={setOpacity} />
          )}
        </div>

        {/* Flood */}
        <div className="flex flex-col gap-y-2 px-4 py-3">
          <label className="flex justify-between" htmlFor="flood">
            <Typography variant="body2">Flood</Typography>
            <Toggle
              active={floodActive}
              id="flood"
              setActive={(val) => {
                if (val) {
                  setSoilsActive(false);
                  setElevationActive(false);
                }
                setFloodActive(val);
              }}
            />
            {floodActive && <FloodOverlay opacity={opacity} />}
          </label>
          {/* Opacity Slider */}
          {floodActive && (
            <OpacityChanger value={opacity} onChange={setOpacity} />
          )}
        </div>
      </div>
    </div>
  );
};
